import { cn } from "~/../lib/utils";
import { NavLink, Outlet } from "react-router";
import { ModeToggle } from "~/components/common/mode-toggle";
import { useMemo, useState } from "react";
import {
  ArrowDownFromLine,
  ArrowUpFromLine,
  UserIcon,
  Menu,
  CodeIcon, ChevronUp, ChevronDown,
} from "lucide-react";
import { Dialog } from "../common/Dialog";
import { Button } from "../ui/button";
import { useTranslation } from "react-i18next";
import { SessionSettingsForm } from "~/components/forms/session-settings-form";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { useResolvedMode } from "~/hooks/resolved-mode-hook";
import useUserState from "~/stores/user";
import useSettingState from "~/stores/setting";
import type { MainLayoutProps, ScRoute, UserInfo } from "~/types/ui";
import { getConfig } from "lib/config";

export default function MainLayout({
  logo,
  routes,
  user,
  onCompanyClick,
}: MainLayoutProps) {
  const permissions = useUserState((s) => s.permissions);
  const permitted = useUserState((s) => s.isPermitted);
  const isDark = useResolvedMode() === "dark";

  const navItems = useMemo(() => {
    // Recursive because NavItem renders its own children recursively: filtering only the
    // first two levels leaves anything deeper — every entry under "Manage" — unchecked.
    const allowed = (items: ScRoute[]): ScRoute[] =>
      items
        .filter((r) => !r.permission || permitted(r.permission))
        .map((r) => {
          if (!r.children) return r;

          const children = allowed(r.children);
          // Skip parent if no children are permitted and has children
          return children.length > 0 ? { ...r, children } : null;
        })
        .filter((r) => r !== null);

    let resultNavItems = allowed(routes).map((r) => {
      return (
        <NavItem
          key={r.label}
          label={r.label}
          to={r.to}
          icon={r.icon}
          children={r.children ?? []}
        />
      );
    });

    let swaggerUrl = getConfig().BASE_URL;
    if (swaggerUrl.endsWith("/api"))
      swaggerUrl = swaggerUrl.substring(0, swaggerUrl.length - "/api".length);
    swaggerUrl += "/swagger";

    resultNavItems.push(
      <NavItem
        key={"common.swagger"}
        label={"common.swagger"}
        to={swaggerUrl}
        icon={<CodeIcon className="w-5 h-5" />}
      />,
    );

    return resultNavItems;
  }, [routes, permissions]);

  const [sessionOpen, setSessionOpen] = useState(false);
  const toggleDialog = () => setSessionOpen(!sessionOpen);

  return (
    <div className="h-screen w-screen bg-background text-foreground flex flex-col">
      {/* Mobile Header with Hamburger */}
      <div className="flex md:hidden items-center justify-between px-4 py-2 border-b shrink-0">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" aria-label="Open menu">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-64 overflow-y-scroll">
            <SidebarContent
              logo={logo}
              isDark={isDark}
              navItems={navItems}
              user={user}
              onCompanyClick={onCompanyClick}
              sessionOpen={sessionOpen}
              setSessionOpen={setSessionOpen}
              toggleDialog={toggleDialog}
            />
          </SheetContent>
        </Sheet>

        {logo ? (
          logo
        ) : (
          <img
            src={isDark ? "/sensor-online-dark.svg" : "/sensor-online.svg"}
            alt="Logo"
            className="h-8"
          />
        )}
      </div>

      {/* Desktop Layout */}
      <div className="flex flex-1 overflow-hidden ">
        {/* Sidebar (Desktop only) */}
        <aside className="hidden md:flex w-64 border-r flex-col bg-sidebar shrink-0 ">
          <SidebarContent
            logo={logo}
            isDark={isDark}
            navItems={navItems}
            user={user}
            onCompanyClick={onCompanyClick}
            sessionOpen={sessionOpen}
            setSessionOpen={setSessionOpen}
            toggleDialog={toggleDialog}
          />
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-hidden overflow-y-scroll overscroll-y-contain">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function SidebarContent({
  logo,
  isDark,
  navItems,
  user,
  onCompanyClick,
  sessionOpen,
  setSessionOpen,
  toggleDialog,
}: {
  logo?: React.ReactNode;
  isDark: boolean;
  navItems: React.ReactNode[];
  user: UserInfo | null;
  onCompanyClick?: () => void;
  sessionOpen: boolean;
  setSessionOpen: (open: boolean) => void;
  toggleDialog: () => void;
}) {
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 overflow-y-scroll no-scrollbar">
        <div className="text-xl font-bold mb-6">
          {logo ? (
            logo
          ) : (
            <img
              src={isDark ? "/sensor-online-dark.svg" : "/sensor-online.svg"}
              alt="Logo"
              className="h-12 w-full object-contain"
            />
          )}
        </div>

        <nav className="flex flex-col space-y-1">{navItems}</nav>
      </div>

      {/* Push user info + theme toggle to bottom */}
      {user && (
        <div className="border-t border-border p-4 mt-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Dialog
                title="Session Settings"
                description="Manage your session settings"
                open={sessionOpen}
                onOpenChange={setSessionOpen}
                trigger={
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Session settings"
                  >
                    <UserIcon className="h-[1.2rem] w-[1.2rem]" />
                  </Button>
                }
                children={<SessionSettingsForm onSubmit={toggleDialog} />}
              />

              <div className="flex flex-col">
                <span className="text-sm font-semibold">{user.fullName}</span>
                <span
                  className="text-xs text-muted-foreground cursor-pointer"
                  onClick={onCompanyClick}
                >
                  {user.companyName} - {user.companyId}
                </span>
              </div>
            </div>

            <ModeToggle />
          </div>
        </div>
      )}
    </div>
  );
}

function updateTitle(title: string) {
  const branding = useSettingState.getState().getDocumentTitle();
  document.title = `${title === "" ? "" : title + " - "}${branding}`;
}

function NavItem({
  to,
  icon,
  label,
  children,
  isChild = false,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  children?: ScRoute[];
  isChild?: boolean;
}) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <NavLink
        end={isChild}
        to={to}
        onClick={(e) => {
          if (children && children.length > 0) {
            e.preventDefault();
            setIsExpanded(!isExpanded);
            updateTitle("");
            return;
          }

          updateTitle(t(label));
        }}
        className={({ isActive }: { isActive: boolean }) =>
          cn(
            "w-full flex items-center gap-3 px-2 py-2 rounded-md text-xs font-medium transition-colors mt-2",
            isActive
              ? "bg-primary text-primary-foreground"
              : "hover:underline hover:text-accent-foreground",
          )
        }
      >
        {icon}
        {t(label)}
        <div className="flex-1 text-right">
          {children &&
            children.length > 0 &&
            (isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            ))}
        </div>
      </NavLink>

      {children && children.length > 0 && isExpanded && (
        <div className="pl-4">
          {children.map((child) => (
            <NavItem
              key={child.label}
              to={child.to}
              icon={child.icon}
              label={child.label}
              children={child.children}
              isChild={true}
            />
          ))}
        </div>
      )}
    </>
  );
}
